import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { MapPin, Package, Globe, CheckCircle2, Phone, Truck, ShieldCheck, Monitor, Car, Stethoscope, Briefcase, ArrowRight } from 'lucide-react';
export function Shipping() {
  return <div className="bg-warm-50 min-h-screen pb-20" data-id="element-1115">
      {/* Header */}
      <div className="bg-primary-900 text-white py-16 lg:py-24" data-id="element-1116">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1117">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1118">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" data-id="element-1119">
              International Shipping to Nigeria
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto leading-relaxed" data-id="element-1120">
              Reliable and affordable shipping from Washington DC, Maryland
              &amp; Virginia to Lagos, Nigeria. We handle your packages with
              care from pickup to delivery.
            </p>
          </motion.div>
        </div>
      </div>

      {/* How It Works */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1121">
        <div className="text-center mb-16" data-id="element-1122">
          <h2 className="text-3xl font-bold text-warm-900 mb-4" data-id="element-1123">
            How It Works
          </h2>
          <p className="text-lg text-warm-600 max-w-2xl mx-auto" data-id="element-1124">
            Three simple steps to get your items safely to Lagos.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1125">
          {[{
          step: '1',
          icon: <Package className="w-8 h-8" data-id="element-1126" />,
          title: 'Request a Quote',
          desc: 'Contact us with your package details for an affordable, transparent shipping quote.'
        }, {
          step: '2',
          icon: <Truck className="w-8 h-8" data-id="element-1127" />,
          title: 'We Pick Up',
          desc: 'We collect your items directly from your location in DC, MD, or VA.'
        }, {
          step: '3',
          icon: <Globe className="w-8 h-8" data-id="element-1128" />,
          title: 'Delivered to Lagos',
          desc: 'Your items arrive safely in Lagos, ready for pickup or local delivery.'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1,
          duration: 0.5
        }} className="text-center" data-id="element-1129">
              <div className="relative" data-id="element-1130">
                <div className="w-20 h-20 mx-auto bg-white rounded-2xl shadow-md flex items-center justify-center text-primary-600 mb-6 relative z-10" data-id="element-1131">
                  {item.icon}
                </div>
                {i < 2 && <div className="hidden md:block absolute top-10 left-[60%] w-[80%] h-[2px] bg-warm-200 -z-0" data-id="element-1132"></div>}
              </div>
              <h3 className="text-xl font-bold text-warm-900 mb-3" data-id="element-1133">
                {item.step}. {item.title}
              </h3>
              <p className="text-warm-600 leading-relaxed" data-id="element-1134">{item.desc}</p>
            </motion.div>)}
        </div>
      </div>

      {/* What We Ship */}
      <div className="bg-white py-20" data-id="element-1135">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1136">
          <div className="text-center mb-16" data-id="element-1137">
            <h2 className="text-3xl font-bold text-warm-900 mb-4" data-id="element-1138">
              What We Ship
            </h2>
            <p className="text-lg text-warm-600 max-w-2xl mx-auto" data-id="element-1139">
              From personal items to commercial cargo, we handle a wide variety
              of shipments.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1140">
            {[{
            icon: <div className="w-6 h-6" data-id="element-1141" />,
            title: 'Personal Items'
          }, {
            icon: <Package className="w-6 h-6" data-id="element-1142" />,
            title: 'Household Goods'
          }, {
            icon: <Monitor className="w-6 h-6" data-id="element-1143" />,
            title: 'Electronics'
          }, {
            icon: <Car className="w-6 h-6" data-id="element-1144" />,
            title: 'Auto Parts'
          }, {
            icon: <Stethoscope className="w-6 h-6" data-id="element-1145" />,
            title: 'Medical Equipment'
          }, {
            icon: <Briefcase className="w-6 h-6" data-id="element-1146" />,
            title: 'Business Cargo'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05,
            duration: 0.4
          }} data-id="element-1147">
                <Card className="border border-warm-100 hover:border-primary-300 transition-colors shadow-sm hover:shadow-md" data-id="element-1148">
                  <CardBody className="p-6 flex items-center gap-4" data-id="element-1149">
                    <div className="bg-primary-50 p-3 rounded-lg text-primary-600" data-id="element-1150">
                      {item.icon}
                    </div>
                    <h3 className="font-bold text-warm-900" data-id="element-1151">{item.title}</h3>
                  </CardBody>
                </Card>
              </motion.div>)}
          </div>
        </div>
      </div>

      {/* Shipping Routes & Why Choose Us */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-id="element-1152">
        <div className="grid lg:grid-cols-2 gap-16" data-id="element-1153">
          {/* Routes */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1154">
            <h2 className="text-3xl font-bold text-warm-900 mb-8" data-id="element-1155">
              Our Shipping Routes
            </h2>
            <div className="space-y-8" data-id="element-1156">
              <div className="bg-white p-6 rounded-2xl shadow-md border border-warm-100" data-id="element-1157">
                <div className="flex items-center gap-3 mb-4" data-id="element-1158">
                  <div className="bg-green-100 p-2 rounded-full text-green-600" data-id="element-1159">
                    <MapPin className="w-5 h-5" data-id="element-1160" />
                  </div>
                  <h3 className="text-xl font-bold text-warm-900" data-id="element-1161">
                    Pickup Locations
                  </h3>
                </div>
                <ul className="space-y-4 ml-11" data-id="element-1162">
                  <li data-id="element-1163">
                    <strong className="text-warm-900 block mb-1" data-id="element-1164">
                      Washington D.C.
                    </strong>
                    <span className="text-warm-600 text-sm" data-id="element-1165">
                      Full metro area coverage
                    </span>
                  </li>
                  <li data-id="element-1166">
                    <strong className="text-warm-900 block mb-1" data-id="element-1167">
                      Maryland
                    </strong>
                    <span className="text-warm-600 text-sm" data-id="element-1168">
                      Silver Spring, Bethesda, Rockville, College Park, Bowie,
                      Laurel, Germantown
                    </span>
                  </li>
                  <li data-id="element-1169">
                    <strong className="text-warm-900 block mb-1" data-id="element-1170">
                      Northern Virginia
                    </strong>
                    <span className="text-warm-600 text-sm" data-id="element-1171">
                      Alexandria, Arlington, Fairfax, Reston
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex justify-center text-primary-400 py-2" data-id="element-1172">
                <ArrowRight className="w-8 h-8 rotate-90 lg:rotate-0" data-id="element-1173" />
              </div>

              <div className="bg-primary-900 text-white p-6 rounded-2xl shadow-lg" data-id="element-1174">
                <div className="flex items-center gap-3 mb-2" data-id="element-1175">
                  <div className="bg-primary-800 p-2 rounded-full text-primary-300" data-id="element-1176">
                    <Globe className="w-5 h-5" data-id="element-1177" />
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-1178">Destination</h3>
                </div>
                <div className="ml-11" data-id="element-1179">
                  <strong className="text-2xl block mb-1" data-id="element-1180">
                    Lagos, Nigeria
                  </strong>
                  <span className="text-primary-200" data-id="element-1181">
                    Secure arrival and handling
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Choose Us */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1182">
            <h2 className="text-3xl font-bold text-warm-900 mb-8" data-id="element-1183">
              Why Choose Our Service?
            </h2>
            <div className="bg-white p-8 rounded-2xl shadow-md border border-warm-100 h-full" data-id="element-1184">
              <ul className="space-y-6" data-id="element-1185">
                {[{
                title: 'Affordable Rates',
                desc: 'Competitive pricing with no hidden fees. Get exactly what you pay for.'
              }, {
                title: 'Secure Packaging',
                desc: 'We ensure your items are properly secured for the long journey.'
              }, {
                title: 'Door-to-Door Service',
                desc: 'Convenient pickup from your home or business in the DMV area.'
              }, {
                title: 'Tracking Available',
                desc: 'Stay updated on your shipment status from departure to arrival.'
              }, {
                title: 'Experienced Team',
                desc: 'Years of experience handling international logistics to Nigeria.'
              }].map((item, i) => <li key={i} className="flex items-start" data-id="element-1186">
                    <CheckCircle2 className="w-6 h-6 text-primary-600 mr-4 flex-shrink-0 mt-0.5" data-id="element-1187" />
                    <div data-id="element-1188">
                      <h4 className="font-bold text-warm-900 mb-1" data-id="element-1189">
                        {item.title}
                      </h4>
                      <p className="text-warm-600 text-sm leading-relaxed" data-id="element-1190">
                        {item.desc}
                      </p>
                    </div>
                  </li>)}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-primary-600 py-16 mt-10" data-id="element-1191">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1192">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1193">
            Ready to Ship to Lagos?
          </h2>
          <p className="text-primary-100 text-lg mb-8 max-w-2xl mx-auto" data-id="element-1194">
            Contact us for a free shipping quote. We offer competitive rates for
            all package sizes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1195">
            <Link to="/booking" data-id="element-1196">
              <Button size="lg" variant="ghost" className="!bg-white !text-primary-900 hover:!bg-warm-50 w-full sm:w-auto" data-id="element-1197">
                Request a Quote
              </Button>
            </Link>
            <a href="tel:2404132157" data-id="element-1198">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto" data-id="element-1199">
                <Phone className="w-5 h-5 mr-2" data-id="element-1200" />
                Call (240) 413-2157
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>;
}